import {
  createTheme,
  responsiveFontSizes,
  inputBaseClasses,
  popoverClasses,
  appBarClasses,
  alpha,
} from "@mui/material";

const breakpoints = {
  xs: 0,
  sm: 600,
  md: 900,
  lg: 1024,
  xl: 1215,
};

export const theme = responsiveFontSizes(
  createTheme({
    breakpoints: {
      values: breakpoints,
    },
    palette: {
      primary: {
        main: "#035C67",
      },
      secondary: {
        main: "#23085A",
      },
      text: {
        primary: "#000000",
        secondary: "#54565A",
      },
      common: {
        white: "#ffffff",
        black: "#000000",
      },
      success: {
        main: "#00953B",
        light: "#5DEB4B",
      },
      error: {
        main: "#AB0033",
      },
      warning: {
        main: "#FF8200",
      },
      grey: {
        50: "#fafafa",
        100: "#F4F4F4",
        200: "#eeeeee",
        300: "#E5E5E5",
        400: "#c1c1c1",
        500: "#898A8D",
        600: "#757575",
        700: "#616161",
        800: "#424242",
        900: "#212121",
        A100: "#f5f5f5",
        A200: "#eeeeee",
        A400: "#bdbdbd",
        A700: "#616161",
      },
      blue: {
        main: "#00DCFF",
        dark: "#005CB9",
        light: "#97DAFF",
      },
      teal: {
        main: "#00EDC3",
        dark: "#009D8C",
        light: "#99EDD1",
      },
      petrol: {
        main: "#035C67",
        dark: "#003B4A",
        light: "#7CA7AD",
      },
    },

    typography: {
      fontFamily: ["Arial", "Helvetica Neue", "Helvetica", "sans-serif"].join(
        ","
      ),
      h1: {
        color: "#23085A",
        fontFamily: "BCSansHeading",
        fontSize: "2.5rem",
        fontWeight: 400,
      },
      h2: {
        color: "#23085A",
        fontFamily: "BCSans",
        fontSize: "1.75rem",
        fontWeight: 700,
      },
      h3: {
        color: "#23085A",
        fontFamily: "Arial",
        fontSize: "1.5rem",
        fontWeight: 700,
      },
      h4: {
        color: "#23085A",
        fontWeight: "bold",
        fontSize: "1.25rem",
      },
      h5: {
        color: "#23085A",
        fontSize: "0.875rem",
        fontWeight: "bold",
      },
      h6: {
        fontWeight: "bold",
        fontSize: "1rem",
      },
      body1: {
        fontSize: "1rem",
      },
      body2: {
        fontSize: "0.875rem",
      },
      button: {
        fontSize: "1rem",
        textTransform: "none",
        fontWeight: "bold",
      },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: `
      @font-face {
        font-family: BCSans;
        src: url(https://bccdn.britishcouncil.org/apps/entrynet/fonts/BritishCouncilSansW08-Bd.woff2) format('woff2'),
          url(https://bccdn.britishcouncil.org/apps/entrynet/fonts/BritishCouncilSansW08-Bd.woff) format('woff'),
          local('Arial');
        font-weight: 600;
        font-display: swap;
      }

      @font-face {
        font-family: BCSans;
        src: url(https://bccdn.britishcouncil.org/apps/entrynet/fonts/BritishCouncilSansW08-Rg.woff2) format('woff2'),
          url(https://bccdn.britishcouncil.org/apps/entrynet/fonts/BritishCouncilSansW08-Rg.woff) format('woff'),
          local('Arial');
        font-weight: 300;
        font-style: swap;
      }

      @font-face {
        font-family: BCSans;
        src: url(https://bccdn.britishcouncil.org/apps/entrynet/fonts/BritishCouncilSansW08-Lt.woff2) format('woff2'),
          url(https://bccdn.britishcouncil.org/apps/entrynet/fonts/BritishCouncilSansW08-Lt.woff) format('woff'),
          local('Arial');
        font-weight: 100;
        font-display: swap;
      }
  
      @font-face {
        font-family: BCSansHeading;
        src: url(https://bccdn.britishcouncil.org/apps/entrynet/fonts/BritishCouncilSansW05-Headline.woff2) format('woff2'),
          url(https://bccdn.britishcouncil.org/apps/entrynet/fonts/BritishCouncilSansW05-Headline.woff) format('woff'),
          local('Arial');
        font-weight: 400;
        font-display: swap;
      }
      
      ul {
        margin: 0
      }

      body, html {
        width: 100%;
        height: 100%;
      }

      body div#root {
        height: 100%;
      }
      `,
      },
      MuiAppBar: {
        styleOverrides: {
          root: ({ theme }) => ({
            backgroundColor: theme.palette.secondary.main,
            borderRadius: 0,
          }),
        },
      },
      MuiToolbar: {
        styleOverrides: {
          root: ({ theme }) => ({
            "&:not(.MuiTablePagination-toolbar)": {
              backgroundColor: theme.palette.secondary.main,
            },
            "&.MuiTablePagination-toolbar": {
              backgroundColor: theme.palette.grey[100],
            },
          }),
        },
      },
      MuiLink: {
        styleOverrides: {
          root: ({ theme }) => ({
            color: theme.palette.teal.dark,
            fontWeight: "700",
            display: "flex",
          }),
        },
      },
      MuiTableHead: {
        styleOverrides: {
          root: ({ theme }) => ({
            backgroundColor: theme.palette.grey[100],
            color: theme.palette.secondary.main,
          }),
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: ({ theme }) => ({
            color: "#000000CC",
            paddingLeft: "0.5rem",
            paddingRight: "0.5rem",
            "&:first-of-type": {
              paddingLeft: "1rem",
            },
            "&:last-of-type": {
              paddingRight: "1rem",
            },
            "&.MuiTableCell-head": {
              color: theme.palette.secondary.main,
              fontWeight: "700",
            },
            "&:not(.MuiTableCell-head)": {
              textAlign: "center",
            },
          }),
        },
      },
      MuiTableContainer: {
        styleOverrides: {
          root: () => ({
            borderTopLeftRadius: "8px",
            borderTopRightRadius: "8px",
          }),
        },
      },
      MuiTablePagination: {
        styleOverrides: {
          root: () => ({
            borderBottomLeftRadius: "8px",
            borderBottomRightRadius: "8px",
          }),
        },
      },
      MuiPaper: {
        defaultProps: {
          elevation: 2,
        },
        styleOverrides: {
          root: () => ({
            [`&:not(.${popoverClasses.paper}):not(.${appBarClasses.root})`]: {
              borderRadius: "8px",
            },
          }),
        },
      },
      MuiInputBase: {
        styleOverrides: {
          input: () => ({
            [`&.${inputBaseClasses.input}`]: {
              paddingTop: "12.5px",
              paddingBottom: "12.5px",
            },
          }),
        },
      },
      MuiRadio: {
        styleOverrides: {
          root: ({ theme }) => ({
            height: "44px",
            width: "44px",
            border: `1px solid ${theme.palette.grey[500]}`,
            flexShrink: 0,
            color: theme.palette.grey[500],
            svg: {
              height: "16px",
              width: "16px",
            },
            ".MuiFormControlLabel-root:has(> &)": {
              marginLeft: 0,
              gap: "16px",
            },
            "&.Mui-checked": {
              border: `1px solid ${theme.palette.primary.main}`,
              color: theme.palette.primary.main,
              background: `linear-gradient(0deg, ${theme.palette.primary.main}1A, ${theme.palette.primary.main}1A),linear-gradient(0deg, ${theme.palette.primary.main}1A, ${theme.palette.primary.main}1A),linear-gradient(0deg, #FFFFFF, #FFFFFF)`,
              svg: {
                "&:nth-of-type(2)": {
                  transform: "scale(1.4)",
                },
              },
            },
          }),
        },
      },
      MuiFormHelperText: {
        styleOverrides: {
          root: ({ theme }) => ({
            "&.Mui-error": {
              position: "relative",
              marginLeft: 0,
              paddingLeft: "10px",
              fontWeight: "700",
              lineHeight: 2,
              "&:before": {
                content: "''",
                display: "block",
                position: "absolute",
                left: 0,
                top: 0,
                bottom: 0,
                width: "5px",
                background: theme.palette.error.main,
                borderRadius: "3px",
              },
            },
          }),
        },
      },
      MuiFormLabel: {
        styleOverrides: {
          root: ({ theme }) => ({
            "&.Mui-error, &.Mui-focused": {
              color: theme.palette.text.secondary,
            },
          }),
          asterisk: ({ theme }) => ({
            "&.Mui-error, &.Mui-focused": {
              color: theme.palette.text.secondary,
            },
          }),
        },
      },
      MuiChip: {
        variants: [
          {
            props: { size: "large" },
            style: { fontSize: "1rem" },
          },
          //add more color variants if needed
          {
            props: { variant: "colored", color: "error" },
            style: ({ theme }) => ({
              backgroundColor: alpha(theme.palette.error.main, 0.1),
              color: theme.palette.error.main,
              border: 0,
            }),
          },
          {
            props: { variant: "colored", color: "success" },
            style: ({ theme }) => ({
              backgroundColor: alpha(theme.palette.success.main, 0.1),
              color: theme.palette.success.main,
              border: 0,
            }),
          },
          {
            props: { variant: "colored", color: "warning" },
            style: ({ theme }) => ({
              backgroundColor: alpha(theme.palette.warning.main, 0.1),
              color: theme.palette.warning.main,
              border: 0,
            }),
          },
          {
            props: { variant: "colored", color: "info" },
            style: ({ theme }) => ({
              backgroundColor: alpha(theme.palette.info.main, 0.1),
              color: theme.palette.info.main,
              border: 0,
            }),
          },
          {
            props: { variant: "colored", color: "primary" },
            style: ({ theme }) => ({
              backgroundColor: alpha(theme.palette.primary.main, 0.1),
              color: theme.palette.primary.main,
              border: 0,
            }),
          },
        ],
      },
      MuiButton: {
        styleOverrides: {
          root: ({ theme }) => ({
            fontSize: "16px",
            outline: "none",
            fontWeight: "700",
            padding: "12px 20px",
            borderRadius: "8px",
            textTransform: "none",
            lineHeight: "1.5",
            "&.MuiButton-outlined": {
              borderWidth: "2px",
            },
            "&.MuiButton-outlined.MuiButton-colorPrimary": {
              borderColor: theme.palette.primary.main,
            },
          }),
        },
      },
    },
  })
);
